// src/components/Notification/NotificationManager.tsx
import React, { useState, createContext, useContext, ReactNode, useCallback } from 'react';
import Notification, { NotificationType, NotificationProps } from './index';

// 定义通过 Context 提供的 API
interface NotificationApi {
    showNotification: (options: Omit<NotificationProps, 'id' | 'onClose'>) => void;
    hideNotification: (id: string | number) => void;
}

// 创建 Context，并提供默认值（用于在非 Provider 包裹时给出友好提示）
const NotificationContext = createContext<NotificationApi>({
    showNotification: () => console.error('NotificationProvider not found'),
    hideNotification: () => console.error('NotificationProvider not found'),
});

// Provider 组件
interface NotificationProviderProps {
    children: ReactNode;


}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
    const [notifications, setNotifications] = useState<NotificationProps[]>([]);

    // 显示通知
    const showNotification = useCallback((options: Omit<NotificationProps, 'id' | 'onClose'>) => {
        // 自动生成唯一 ID
        const newNotification: NotificationProps = {
            id: crypto.randomUUID(),
            ...options,
            // onClose 回调在这里被绑定，子组件无需关心
            onClose: hideNotification,
        };
        // 新通知添加到数组开头，实现从上到下的堆叠
        setNotifications((prev) => [newNotification, ...prev]);
    }, []);

    // 隐藏通知
    const hideNotification = useCallback((id: string | number) => {
        setNotifications((prev) => prev.filter((note) => note.id !== id));
    }, []);

    // 提供给子组件的 API
    const contextValue: NotificationApi = {
        showNotification,
        hideNotification,
    };

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            {/* 通知容器，固定在右上角 */}
            <div className="fixed top-10 right-4 z-50 flex flex-col gap-2 items-end">
                {notifications.map((note) => (
                    <Notification
                        key={note.id}
                        {...note}
                    />
                ))}
            </div>
        </NotificationContext.Provider>
    );
};

// 自定义 Hook，方便组件使用通知 API
export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

// 导出 Context，方便在需要的地方直接使用 useContext(NotificationContext)
export default NotificationContext;
